import { BlogPost, BlogCategory } from '../types/blog';

// WordPress REST API - using Vite proxy
const WP_API_BASE = '/wp-api';

function calculateReadingTime(text: string) {
  const plainText = text.replace(/<[^>]*>/g, '');
  const words = plainText.trim().split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return {
    text: `${minutes} min read`,
    minutes,
    time: minutes * 60 * 1000,
    words
  };
}

function mapCategory(categoryId: number): BlogCategory {
  // Map WordPress category IDs to BlogCategory
  const categoryMap: Record<number, BlogCategory> = {
    2: BlogCategory.FUNDAMENTALS,
    3: BlogCategory.PRACTICAL_GUIDES,
    4: BlogCategory.TOOLS,
    5: BlogCategory.BUSINESS,
    6: BlogCategory.LEGAL,
    7: BlogCategory.FRAMEWORKS,
  };
  return categoryMap[categoryId] || BlogCategory.FUNDAMENTALS;
}

export async function getAllPosts(): Promise<BlogPost[]> {
  console.log('getAllPosts: Starting fetch from WordPress...');

  try {
    const url = `${WP_API_BASE}/posts&per_page=100`;
    console.log('getAllPosts: Fetching from URL:', url);

    const response = await fetch(url);
    console.log('getAllPosts: Response status:', response.status);

    if (!response.ok) {
      console.error('getAllPosts: Response not OK:', response.statusText);
      return [];
    }

    const wpPosts = await response.json();
    console.log('getAllPosts: Received posts:', wpPosts.length);

    const posts: BlogPost[] = wpPosts.map((post: any) => {
      const description = post.excerpt?.rendered
        ?.replace(/<[^>]*>/g, '')
        ?.replace(/\n/g, ' ')
        ?.trim() || '';

      return {
        slug: post.slug,
        title: post.title?.rendered?.replace(/&#8217;/g, "'").replace(/&#038;/g, "&").replace(/&amp;/g, "&") || 'Untitled',
        description: description.substring(0, 200),
        author: 'Accessibility Team',
        date: post.date?.split('T')[0] || new Date().toISOString().split('T')[0],
        category: mapCategory(post.categories?.[0] || 1),
        tags: [],
        readingTime: calculateReadingTime(post.content?.rendered || ''),
        featured: false,
        content: post.content?.rendered || ''
      };
    });

    console.log('getAllPosts: Mapped posts:', posts.length);
    return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  } catch (error) {
    console.error('getAllPosts: Error:', error);
    return [];
  }
}

export async function getPostBySlug(slug: string): Promise<BlogPost | null> {
  console.log('getPostBySlug: Looking for slug:', slug);

  try {
    const url = `${WP_API_BASE}/posts&slug=${encodeURIComponent(slug)}`;
    console.log('getPostBySlug: Fetching from URL:', url);

    const response = await fetch(url);

    if (!response.ok) {
      console.error('getPostBySlug: Response not OK');
      return null;
    }

    const wpPosts = await response.json();

    if (!wpPosts || wpPosts.length === 0) {
      console.log('getPostBySlug: No post found');
      return null;
    }

    const post = wpPosts[0];
    const description = post.excerpt?.rendered
      ?.replace(/<[^>]*>/g, '')
      ?.replace(/\n/g, ' ')
      ?.trim() || '';

    return {
      slug: post.slug,
      title: post.title?.rendered?.replace(/&#8217;/g, "'").replace(/&#038;/g, "&").replace(/&amp;/g, "&") || 'Untitled',
      description: description.substring(0, 200),
      author: 'Accessibility Team',
      date: post.date?.split('T')[0] || new Date().toISOString().split('T')[0],
      category: mapCategory(post.categories?.[0] || 1),
      tags: [],
      readingTime: calculateReadingTime(post.content?.rendered || ''),
      featured: false,
      content: post.content?.rendered || ''
    };
  } catch (error) {
    console.error('getPostBySlug: Error:', error);
    return null;
  }
}

export async function getPostsByCategory(category: BlogCategory): Promise<BlogPost[]> {
  const posts = await getAllPosts();
  return posts.filter(post => post.category === category);
}

export async function getFeaturedPosts(): Promise<BlogPost[]> {
  const posts = await getAllPosts();
  return posts.slice(0, 3); // Return first 3 as featured
}

export async function getRelatedPosts(currentPost: BlogPost, limit: number = 3): Promise<BlogPost[]> {
  const posts = await getAllPosts();
  return posts
    .filter(post => post.slug !== currentPost.slug)
    .slice(0, limit);
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

export function getCategoryColor(category: BlogCategory): string {
  const colors: Record<BlogCategory, string> = {
    [BlogCategory.FUNDAMENTALS]: 'bg-blue-100 text-blue-800',
    [BlogCategory.PRACTICAL_GUIDES]: 'bg-green-100 text-green-800',
    [BlogCategory.TOOLS]: 'bg-purple-100 text-purple-800',
    [BlogCategory.BUSINESS]: 'bg-orange-100 text-orange-800',
    [BlogCategory.LEGAL]: 'bg-red-100 text-red-800',
    [BlogCategory.FRAMEWORKS]: 'bg-indigo-100 text-indigo-800'
  };
  return colors[category] || 'bg-gray-100 text-gray-800';
}
